import React, { useEffect, useState } from 'react';

import { getFirstMatchingTag } from 'src/components/pageMeta/ImageMappingHandler';
import Rails from 'src/components/rails/Rails';
import {
  RailsSkeletonCount,
  identifyTagType
} from 'src/components/rails/railsHelper';
import {
  ConsumptionPageTypes,
  PersonalizedContent
} from 'src/types/consumption';
import {
  API_STATUS_TYPES,
  CONSUMPTION_PAGE_STRING_CONSTANTS
} from 'src/utils/constants/utilConstants';
import fetchNextPage from 'src/utils/IntersectionObserverHelper';
import { titleGenerator } from 'src/utils/routeHandler';

import { fetchPersonalisedContent } from '../../../services/apiRequests/MovieConsumptionPage/fetchPersonalisedContent';
import SimpleStore from '../../../store/SimpleStore';
import { HTTP_URL_PARAMS } from '../../../utils/constants/HttpMethodsConstants';
import BellyBannerRail from '../../rails/BellyBannerRail';
import SkeletonSimilarContentTray from '../../SkeletonLoader/SkeletonSimilarContentTray';
import { ThumbnailTypes } from '../../thumbnail/types';
import { useRouter } from 'next/router';

export type OtherCollectionType = {
  title: string;
  contents: any;
  moreLink: string;
  tagName?: string[];
};
export type RenderRailType = {
  showTitle: boolean;
  title?: string;
  contents: any;
  moreLink?: string;
  thumbnailsToShow: number;
  tagName: string;
  isCollectionOfRails?: boolean;
} | null;
interface CollectionTrayProps {
  similarContents: ConsumptionPageTypes.SimilarContent;
  type: ThumbnailTypes;
  contentInstance: any;
  updateIntersectionStatus: () => void;
}

const CollectionRails: React.FC<CollectionTrayProps> = ({
  similarContents,
  contentInstance,
  updateIntersectionStatus
}) => {
  const [sequenceCollectionIds, setSequenceCollectionIds] = useState(
    similarContents?.relatedCollections?.collections
  );
  const [collectionOfRails, setCollectionOfRails] = useState<any>([]);
  const router = useRouter();
  const [apiStatus, setApiStatus] = useState(API_STATUS_TYPES.idle);
  const [pageCount, setPageCount] = useState(0);

  const renderRelatedCollections = () => {
    if (collectionOfRails && !collectionOfRails.length) {
      return null;
    }

    const rails = collectionOfRails?.length
      ? collectionOfRails.map((collection: any) => {
          const matchingTag = getFirstMatchingTag(collection?.tags);
          const thumbnailType = identifyTagType(matchingTag);
          const { originalTitle, id } = collection;
          const urlTitle = titleGenerator(originalTitle);
          return {
            showTitle: true,
            contents: collection?.rails?.[0]?.contents || collection?.contents,
            tagName: matchingTag,
            thumbnailType,
            title: collection.title,
            moreLink: `/collections/${urlTitle}/${id}`
          };
        })
      : [];
    return [
      {
        showTitle: true,
        contents: rails,
        tagName: '',
        thumbnailType: '',
        title: '',
        moreLink: '',
        isCollectionOfRails: true
      }
    ];
  };

  const renderCollectionOfRails = () => {
    let collectionOfRails: any[] = [];

    /* Related Collections */
    if (similarContents) {
      collectionOfRails = [
        ...collectionOfRails,
        ...(renderRelatedCollections() || [])
      ];
    }

    return collectionOfRails.map((rail, index) => {
      if (rail.isCollectionOfRails) {
        return (
          <section key={index} className="intersectionContainer">
            {rail.contents.map((collection: any, collectionIndex: number) => {
              if (collection?.thumbnailType === ThumbnailTypes.bellybanner) {
                return (
                  <React.Fragment key={collectionIndex}>
                    <BellyBannerRail
                      showTitle={collection?.showTitle}
                      contents={collection?.contents}
                      tagName={collection?.tagName}
                      title={collection?.title}
                      moreLink={collection?.moreLink}
                      verticalIndex={index}
                    />
                  </React.Fragment>
                );
              }
              return (
                <React.Fragment key={collectionIndex}>
                  <Rails
                    showTitle={collection?.showTitle}
                    contents={collection?.contents}
                    tagName={collection?.tagName}
                    thumbnailType={collection?.thumbnailType}
                    title={collection?.title}
                    moreLink={collection?.moreLink}
                    verticalIndex={index}
                    isCollectionResolved={
                      sequenceCollectionIds.length === collectionOfRails.length
                    }
                  />
                </React.Fragment>
              );
            })}
          </section>
        );
      }
      return (
        <Rails
          key={index}
          showTitle={rail?.showTitle}
          contents={rail?.contents}
          tagName={rail?.tagName}
          thumbnailType={rail?.thumbnailType}
          title={rail?.title}
          moreLink={rail?.moreLink}
          verticalIndex={index}
          isCollectionResolved={
            sequenceCollectionIds?.length === collectionOfRails?.length
          }
        />
      );
    });
  };

  const fetchPageData = () => {
    setApiStatus(API_STATUS_TYPES.loading);
  };

  useEffect(() => {
    if (apiStatus === API_STATUS_TYPES.loading) {
      const fetchPageCount = pageCount + 1;
      (async () => {
        const isPersonalizedRail =
          sequenceCollectionIds[fetchPageCount - 1]?.assetSubType ===
          CONSUMPTION_PAGE_STRING_CONSTANTS.PERSONALIZED;
        if (isPersonalizedRail) {
          const movieId: string = (router?.query?.movieId as string) || '';
          const personalizedRail = sequenceCollectionIds[fetchPageCount - 1];
          const personalizationParams = {
            personalizationType:
              personalizedRail?.extended?.personalizationType || '',
            personalizationMetaData:
              personalizedRail?.extended?.personalizationMetaData || '',
            sourceType: CONSUMPTION_PAGE_STRING_CONSTANTS.CONSUMPTIONPAGE,
            contentId: movieId,
            country: SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE) || '',
            languages: SimpleStore.get(HTTP_URL_PARAMS.CONTENT_LANGUAGE) || '',
            translation: SimpleStore.get(HTTP_URL_PARAMS.DISPLAY_LANGUAGE) || ''
          };
          const collection: PersonalizedContent =
            await fetchPersonalisedContent(personalizationParams);
          if (
            collection &&
            collection.personalizedRails?.length &&
            collection.personalizedRails[0]
          ) {
            if (fetchPageCount === 1) {
              setCollectionOfRails([collection?.personalizedRails?.[0]]);
            } else {
              setCollectionOfRails((oldRails: any) => [
                ...oldRails,
                collection &&
                  collection.personalizedRails &&
                  collection.personalizedRails.length &&
                  collection.personalizedRails?.[0]
              ]);
            }
            setApiStatus(API_STATUS_TYPES.success);
          } else {
            setApiStatus(API_STATUS_TYPES.failure);
          }
        } else {
          const collection =
            await contentInstance?.contentInstance?.fetchCollection(
              sequenceCollectionIds[fetchPageCount - 1]?.id,
              contentInstance
            );
          if (collection) {
            if (fetchPageCount === 1) {
              setCollectionOfRails([collection]);
            } else {
              setCollectionOfRails((oldRails: any) => [
                ...oldRails,
                collection
              ]);
            }
            setApiStatus(API_STATUS_TYPES.success);
          } else {
            setApiStatus(API_STATUS_TYPES.failure);
          }
        }
      })();
    }
    if (apiStatus === API_STATUS_TYPES.success) {
      setPageCount((prevCount) => prevCount + 1);
      setApiStatus(API_STATUS_TYPES.idle);
    }
    if (apiStatus === API_STATUS_TYPES.failure) {
      setPageCount((prevCount) => prevCount + 1);
      setApiStatus(API_STATUS_TYPES.loading);
    }
  }, [apiStatus]);

  // fetch next page of collection rails
  useEffect(() => {
    if (!(sequenceCollectionIds && sequenceCollectionIds.length)) {
      return;
    }
    if (sequenceCollectionIds.length === pageCount) {
      fetchNextPage(() => ({}), true, 'intersectionContainer');
      updateIntersectionStatus();
    }
    if (
      pageCount !== 0 &&
      pageCount < sequenceCollectionIds.length &&
      collectionOfRails.length &&
      apiStatus !== API_STATUS_TYPES.loading &&
      apiStatus !== API_STATUS_TYPES.failure
    ) {
      fetchNextPage(fetchPageData, false, 'intersectionContainer');
    }

    if (pageCount === 0 && apiStatus === API_STATUS_TYPES.idle) {
      fetchPageData();
    }
  }, [sequenceCollectionIds, pageCount, collectionOfRails]);

  useEffect(() => {
    if (similarContents) {
      setSequenceCollectionIds(
        similarContents?.relatedCollections?.collections
      );
      setCollectionOfRails([]);
      setPageCount(0);
    }
  }, [similarContents?.id]);

  return (
    <>
      {renderCollectionOfRails()}
      {apiStatus == API_STATUS_TYPES.loading ? (
        <SkeletonSimilarContentTray railCount={RailsSkeletonCount.ONE} />
      ) : null}
    </>
  );
};

export default React.memo(CollectionRails);
